import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/**
 * `<nb-section>` — a padded content section (Mockup `.sec` → `padding:0 18px`).
 *
 * Presentational wrapper. Project a `<nb-section-header>` and content (e.g.
 * a `<nb-stack>`) inside it. Renders a semantic `<section>` element.
 *
 * @example
 * <nb-section>
 *   <nb-section-header over="Nær deg" more="Se alle" (moreClick)="openAll()" />
 *   <nb-stack gap="12px"> … </nb-stack>
 * </nb-section>
 */
@Component({
  selector: 'nb-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<section class="sec"><ng-content /></section>`,
  styleUrl: './section.css',
})
export class NbSection {}

/**
 * `<nb-section-header>` — the `.sec-h` row: an uppercase overline title on the
 * left and an optional accent "more" action on the right.
 *
 * - `over` — the overline label, rendered in the `.over` look (uppercase,
 *   tracked, muted). Rendered as an `<h2>` so it is exposed to AT as the
 *   section heading (base.css zeroes heading margins; `.over` sets the size).
 * - `more` — optional action label. When set, an accent action button is shown
 *   that emits `(more)` on click/keyboard activation.
 *
 * @example
 * <nb-section-header over="Arrangementer" more="Se alle" (moreClick)="seeAll()" />
 */
@Component({
  selector: 'nb-section-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div class="sec-h">
    <h2 class="over">{{ over() }}</h2>
    @if (more(); as label) {
      <button type="button" class="more" (click)="moreClick.emit()">{{ label }}</button>
    }
  </div>`,
  styleUrl: './section.css',
})
export class NbSectionHeader {
  /** Overline title text (rendered uppercase via the `.over` look). */
  readonly over = input.required<string>();
  /** Optional accent action label; when set an action button is shown. */
  readonly more = input<string>();
  /** Emitted when the "more" action is activated. */
  readonly moreClick = output<void>();
}

/**
 * `<nb-divider>` — a 1px hairline separator (Mockup `.divider`).
 *
 * Renders a `<hr>` for native separator semantics (`role="separator"`).
 */
@Component({
  selector: 'nb-divider',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<hr class="divider" />`,
  styleUrl: './section.css',
})
export class NbDivider {}

/**
 * `<nb-stack>` — a vertical flex column (Mockup `.stack`) with a configurable
 * `gap`. Defaults to `0` to match the mockup's gapless stack.
 *
 * @example
 * <nb-stack gap="12px"> … </nb-stack>
 */
@Component({
  selector: 'nb-stack',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--nb-stack-gap]': 'gap()',
  },
  template: `<div class="stack"><ng-content /></div>`,
  styleUrl: './section.css',
})
export class NbStack {
  /** Gap between stacked children as a CSS length. Default `'0'`. */
  readonly gap = input<string>('0');
}
